import { Button, Text } from '@user-portal/wikr-components';
import React, { RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { formatSecondsToFullTime } from 'helpers/date';

import CodeInput from 'components/CodeInput';

import * as S from './styled';

interface PasswordFormProps {
    handleInputChange: (e: React.ChangeEvent<HTMLInputElement>, index: number) => void;
    handleKeyDown: (e: React.KeyboardEvent, index: number) => void;
    handlePaste: (e: React.ClipboardEvent) => void;
    handleResendClick: () => void;
    handleReturnClick: () => void;
    handleLoginClick: () => void;
    isFetching: boolean;
    isError: boolean;
    isCodeComplete: boolean;
    inputRefs: RefObject<HTMLInputElement | null>[];
    code: string[];
    resendTimer: number | null;
    values: Record<string, any>;
}

export const PasswordForm = ({
    handleLoginClick,
    handleResendClick,
    handleReturnClick,
    handleKeyDown,
    handleInputChange,
    handlePaste,
    resendTimer,
    inputRefs,
    isCodeComplete,
    code,
    isError,
    values,
    isFetching,
}: PasswordFormProps) => {
    const { t } = useTranslation();

    return (
        <S.Container>
            <Text type="h4" mb={12} center>
                {t('login.temPass.title')}
            </Text>
            <S.DescriptionText color="text-secondary" center>
                {t('login.temPass.subtitle', { email: values.email })}
            </S.DescriptionText>
            <S.LinkButton onClick={handleReturnClick} data-locator="useDifferentEmailButton">
                <Text>{t('login.temPass.chooseDiffEmail')}</Text>
            </S.LinkButton>

            <CodeInput
                isError={isError}
                code={code}
                handleInputChange={handleInputChange}
                handleKeyDown={handleKeyDown}
                handlePaste={handlePaste}
                inputRefs={inputRefs}
            />
            <S.ErrorText type="caption" bold color="critical-default" isError={isError}>
                {t('basics.codeError')}
            </S.ErrorText>
            <Button
                isLoading={isFetching}
                onClick={handleLoginClick}
                disabled={!isCodeComplete}
                mb={36}
                dataLocator="loginButton"
            >
                {t('basics.login')}
            </Button>

            {resendTimer === null ? (
                <S.LinkButton onClick={handleResendClick} data-locator="resendCodeButton">
                    <Text>{t('basics.resend')}</Text>
                </S.LinkButton>
            ) : (
                <>
                    <Text center color="text-secondary">
                        {t('login.temPass.noEmail')}
                    </Text>
                    <S.TimerBox>
                        <Text color="text-secondary">{t('login.temPass.resend')}</Text>
                        <S.Timer>{formatSecondsToFullTime(resendTimer)}</S.Timer>
                    </S.TimerBox>
                </>
            )}
        </S.Container>
    );
};
